// src/components/formBuilder/TemplatesDrawer.jsx
import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SkeletonList from "./SkeletonList";
import axios from "axios";
import { auth } from "../../firebase-config";

const GET_TEMPLATES_URL =
  process.env.REACT_APP_GET_FORM_TEMPLATES_URL ||
  "https://listformtemplates-774073159618.us-central1.run.app";

function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === "function") {
    try {
      return ts.toDate();
    } catch {}
  }
  if (typeof ts.toMillis === "function") {
    try {
      return new Date(ts.toMillis());
    } catch {}
  }
  if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  if (typeof ts === "number") return Number.isFinite(ts) ? new Date(ts) : null;
  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export default function TemplatesDrawer({ open, onClose, onSelectTemplate }) {
  const [loading, setLoading] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!open) return;
      try {
        setLoading(true);
        setError(null);

        const idToken = await auth.currentUser.getIdToken(true);
        const { data } = await axios.post(
          GET_TEMPLATES_URL,
          {},
          { headers: { Authorization: `Bearer ${idToken}` } }
        );

        if (!mounted) return;

        const list = Array.isArray(data?.templates) ? data.templates : [];

        const normalized = list.map((t) => {
          const d = tsToDate(t.updatedAtMillis);
          return {
            ...t,
            __updatedAtDate: d,
            __updatedAtMillis: d ? d.getTime() : 0,
          };
        });

        normalized.sort((a, b) => b.__updatedAtMillis - a.__updatedAtMillis);
        setTemplates(normalized);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load templates.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [open]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 380 } }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Saved Form Templates
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ p: 2 }}>
        {loading && <SkeletonList rows={8} />}

        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <List dense>
            {templates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No templates found.
              </Typography>
            ) : (
              templates.map((t) => {
                const subtitle = t.__updatedAtDate
                  ? `Last updated: ${t.__updatedAtDate.toLocaleString()}`
                  : "—";

                const status = t.status === "published" ? "published" : "draft";

                return (
                  <ListItemButton key={t.id} onClick={() => onSelectTemplate?.(t.id)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {t.name || t.title || "Untitled"}
                          </Typography>
                          <Chip
                            size="small"
                            label={status === "published" ? "Published" : "Draft"}
                            color={status === "published" ? "success" : "default"}
                            variant={status === "published" ? "filled" : "outlined"}
                          />
                        </Box>
                      }
                      secondary={subtitle}
                    />
                  </ListItemButton>
                );
              })
            )}
          </List>
        )}
      </Box>
    </Drawer>
  );
}